/**
 * confirmation-page.tsx — the authoring surface for a journey end page
 * (stage 3, 2026-09-01). The registry, the state and the migration live in
 * `confirmation-sections.ts` (the two files are deliberately not the same
 * basename — one import path, one module); this file is the three pieces of UI that read
 * them, kept out of NewJourneys.tsx for the same reason appt-setup.tsx is.
 *
 *   · CSRail       the left-pane list of the sections this surface offers —
 *                  what is on the page, and what could be. Eye to hide, and an
 *                  add row for what the type allows but the page does not
 *                  carry. NOT draggable and no remove: reordering and removal
 *                  are the canvas's, where the author can see what moves.
 *   · CSCanvas     the page itself, each section rendered against a sample
 *                  booking and selectable like a block.
 *   · CSInspector  the section scope in the right pane: what the section is,
 *                  and the page-level fields it reads.
 *
 * WHAT A SECTION RENDERS IS NOT AUTHORED. Every value below comes from the
 * booking, which does not exist until a customer makes one — so the canvas
 * shows a SAMPLE and says so. The host supplies it (`CSBooking`); nothing in
 * this file invents content, and no section writes anything except the three
 * page fields the inspector owns.
 *
 * ANSWERS is the one section whose sample cannot be right by construction on a
 * queue or an appointment: which questions were asked depends on the service
 * the customer picked. Stage 4 (2026-09-01) gives it the sample-service
 * switcher the Contact page already had — the SAME control and the same
 * per-page memory, supplied by the host as `answersPick`, so a journey cannot
 * be previewing one service here and another there. The host resolves the
 * questions; this file renders them behind a badge naming the service. With no
 * service picked the section says which control to use rather than showing
 * plausible fake questions. On an event there is nothing to pick — one event,
 * one group — so the host passes no switcher and the preview is exact.
 */
import { Fragment, useState, type ReactNode } from "react";
import { QrCode } from "../../shell/QrCode";
import {
  CONFIRMATION_SECTIONS, csDef, csForType, csMissing, csMove, csToggle, csRemove, csAdd, csApplies,
  type CSState, type CSSection, type CSType, type CSSurface, type ConfirmationSection,
} from "./confirmation-sections";
import { WalletPassBadge } from "../../lib/WalletPassBadge";

/** The canvas's per-section control buttons. Copied in shape from
 *  `block-canvas.tsx`'s CTL on purpose: the two canvases are meant to feel like
 *  one gesture, so the buttons are the same size, colour and radius. */
const CTL = {
  width: 20, height: 20, border: "1px solid #DCCCFC", borderRadius: 4, background: "#FFFFFF",
  color: "#5142C7", fontSize: 10, lineHeight: 1, cursor: "pointer", fontFamily: "inherit",
};

const FONT = '"DM Sans", "Helvetica Neue", system-ui, -apple-system, "Segoe UI", Arial, sans-serif';
const MONO = { fontFamily: "ui-monospace, monospace" };
const SEC = { fontSize: 10, fontWeight: 800, letterSpacing: "0.09em", textTransform: "uppercase" as const, color: "#697180", marginBottom: 8 };
const INPUT = { width: "100%", boxSizing: "border-box" as const, border: "1px solid #D1D4DC", borderRadius: 6, padding: "8px 10px", fontSize: 12.5, fontFamily: FONT, color: "#333333" };
const NOTE = { border: "1px solid #DCCCFC", background: "#F6F2FE", borderRadius: 8, padding: "8px 10px", fontSize: 10.5, lineHeight: 1.5, color: "#5142C7" };
const cap = (s: string) => s.slice(0, 1).toUpperCase() + s.slice(1);

export const safeHttpHref = (value: string): string | null => {
  const href = value.trim();
  if (!/^https?:\/\//i.test(href)) return null;
  try {
    const url = new URL(href);
    return (url.protocol === "http:" || url.protocol === "https:") && !!url.hostname ? href : null;
  } catch {
    return null;
  }
};

/** Resolve a link block without changing legacy documents. A stored `href`
 * always wins, including when it is blank or invalid; falling back to the label
 * in that case could turn rejected author input into a different destination.
 * Blocks written before `href` existed keep their URL-in-text behaviour. */
export const confirmationBlockHref = (block: { text?: unknown; href?: unknown }): string | null => {
  if (Object.prototype.hasOwnProperty.call(block, "href")) {
    return typeof block.href === "string" ? safeHttpHref(block.href) : null;
  }
  return typeof block.text === "string" ? safeHttpHref(block.text) : null;
};

/* ── the sample booking the canvas renders against ────────────────────────
   One shape for all three journey types; a section reads the fields its type
   has and skips the rest. Everything is optional — the host derives what it
   can from the record it is bound to, and a missing value renders as an empty
   state naming the Setup step that sets it, never as a plausible fake. */
export interface CSBooking {
  brandName?: string;
  /** Merged sections (2026-09-02): the checklist lines, and the live queue
   *  figures. Absent renders the section's empty state, never a plausible fake. */
  prepare?: string[];
  queuePos?: string;
  queueWait?: string;
  /** Queue and appointment: the booked service. Event: the event. */
  serviceName?: string;
  eventName?: string;
  dateLine?: string;
  timeLine?: string;
  durationLine?: string;
  advisor?: string;
  ticketLine?: string;
  placeName?: string;
  address?: string;
  /** Phone or video: Place is suppressed at runtime and Meeting link renders. */
  virtual?: boolean;
  /** LOCAL PATCH (platform side, 2026-09-08, JAI-1292): the live join link and passcode, from the SESSION view's virtual
   *  location (null until the server reveals it). The `meeting` arm draws the real
   *  link when present and nothing on the live surface otherwise. */
  joinUrl?: string | null;
  passcode?: string | null;
  reference?: string;
  /** The booking's own record id — what the reference target encodes and what
   *  My Day scans for. Required on the public confirmation response, so unlike
   *  `reference` / `checkInCode` it is present on every real booking. */
  bookingId?: string;
  /** The reception check-in code, exactly as the booking stored it
   *  (`booking.checkInCode` on the public confirmation summary). What the
   *  check-in code section encodes for the reception target. Absent means the
   *  booking has none, and a live page draws no code. */
  checkInCode?: string;
  /** The booking's start, unformatted, plus its zone — so the summary can
   *  render the DATE FORMAT the page chose. `dateLine` stays the host's own
   *  formatting and remains what renders when no format is chosen. */
  startISO?: string;
  /** The booking's end, for the Time row's range under a chosen clock shape. */
  endISO?: string;
  timezone?: string;
  /** The customer's own manage page for this booking — what the check-in code
   *  encodes when the author chooses `manageUrl`. Access-bearing: the booking
   *  id in it is the credential. */
  manageUrl?: string | null;
  guests?: string[];
  answers?: { label: string; value: string }[];
  /** "Sample · Mortgage review" — set when the answers came from a service the
   *  DESIGNER picked rather than from the journey's own group. */
  answersBadge?: string;
  promo?: { title: string; sub?: string } | null;
  footerLines?: string[];
  /** The customer's OWN details, for the yourDetails section (2026-09-07). Held
   *  separately from the tenant-data fields above because these are the only
   *  values on the page that masking touches. */
  customerName?: string;
  customerEmail?: string;
}

/** WHAT THE CHECK-IN CODE ENCODES — an authoring choice, not a constant
 *  (operator, 2026-09-14).
 *
 *  `reference` (default; the saved key is kept so stored pages keep their
 *  choice) means THE BOOKING'S OWN RECORD ID — `bookingId` — for all three
 *  journey types (CD, 2026-09-18). My Day scans it and resolves the record.
 *  The human reference stays printed beside it. It was per-type before, and
 *  neither branch encoded something a scanner resolves: an appointment carried
 *  `checkInCode`, which the anonymous confirmation lane never sends, so that QR
 *  drew nothing at all; an event or queue carried the human reference, which is
 *  a number to read out.
 *
 *  `manageUrl` is the manage link — what a CUSTOMER's phone camera can act on,
 *  since a camera only knows how to open a link. A bare reference does nothing
 *  there. The booking id in the link IS the credential (API line, 2026-09-11),
 *  so whoever scans it can manage the booking; the inspector says that in words
 *  rather than hiding the option, because for a customer-facing code it is the
 *  only choice that works. */
export type CheckinCodeTarget = "reference" | "manageUrl";

export interface CSPageFields { bannerTitle?: string; bannerSub?: string; cancelHours?: string; note?: string; surveyPrompt?: string; checkinCodeTarget?: CheckinCodeTarget; rebookHours?: string; downloadsOff?: string[]; prepare?: string[]; summaryOff?: string[]; summaryLabels?: Record<string, string>; summaryOrder?: string[]; summaryDateFormat?: string; summaryTimeFormat?: string }

/** The summary rows for one journey type, in the AUTHOR's order: the stored
 *  order first (filtered to keys this type actually has), then anything the
 *  stored order does not mention, in the registry's order.
 *
 *  Appending the remainder rather than dropping it is what makes the field
 *  additive: a page authored before a row existed still shows that row, at the
 *  end, instead of losing it to an order that could not have named it. */
export function summaryRowsFor(type: CSType, order?: string[]): { key: string; label: string }[] {
  const all = SUMMARY_ROWS[type];
  if (!order || !order.length) return all;
  const named = order.map((k) => all.find((r) => r.key === k)).filter(Boolean) as { key: string; label: string }[];
  return named.concat(all.filter((r) => order.indexOf(r.key) < 0));
}

/** What HAPPENED to the booking this page is rendering.
 *
 *  The journey end page has only one of these — it is shown seconds after
 *  booking, so the booking is confirmed by construction, and `confirmed` is the
 *  default for that reason. The BOOKING CONFIRMATION PAGE, opened from an
 *  emailed link days or months later, can be any of them, which is why its
 *  designer has a State selector. Wiring it here rather than in that designer is
 *  what keeps one canvas: the outcome is a property of the booking, and the
 *  canvas already renders the booking. */
export type CSPageState = "confirmed" | "cancelled" | "rescheduled" | "past" | "expired" | "closed";

const OUTCOME: Record<CSPageState, { icon: string; fg: string; bg: string; title: string; sub: (hrs: string) => string }> = {
  confirmed:   { icon: "✓", fg: "#1D8348", bg: "#E6F4EE", title: "You're all set", sub: () => "A confirmation is on its way to your email." },
  cancelled:   { icon: "✕", fg: "#D01647", bg: "#FDEEF1", title: "This booking is cancelled", sub: () => "Nothing further is needed from you — you can book again below." },
  rescheduled: { icon: "⇄", fg: "#1B58B8", bg: "#E8F1FD", title: "Your booking has moved", sub: () => "The details below are the new time. The original has been released." },
  past:        { icon: "◷", fg: "#5A6070", bg: "#F0F1F4", title: "This booking has passed", sub: () => "The details below are kept for your records." },
  expired:     { icon: "!", fg: "#B9770E", bg: "#FDF0E3", title: "This link is no longer valid", sub: () => "Request a fresh one from your confirmation email, or contact the branch." },
  closed:      { icon: "◷", fg: "#B9770E", bg: "#FDF0E3", title: "You're all set", sub: (hrs) => `This booking is within ${hrs} hours, so it can no longer be changed online.` },
};

/** On an invalid link there is no booking to show — only who the page is from and
 *  what went wrong. Anything else would be leaking a booking to a URL that has
 *  just been judged not to identify one. */
const EXPIRED_ONLY = ["brandHeader", "banner", "footer"];

/** The sample-service switcher, when the journey type has one to offer. The
 *  host owns the list, the current pick and where it is remembered; passing
 *  `""` to `onPick` clears it. */
export interface CSAnswersPick {
  options: { id: string; name: string }[];
  selected: string;
  onPick: (id: string) => void;
}

/** The rail's control is a CHECKBOX, not an eye (operator, 2026-09-03).
 *
 *  The rail reads as a list of the sections this surface offers, so a row is a
 *  question with two answers: on the page, or not. An eye glyph (◉/◌) answered
 *  it in one direction only — it turned a present section off, but an ABSENT
 *  section's row was a dashed "+ add" affordance, so checking was possible and
 *  unchecking was not. Two different controls for one binary, and the asymmetry
 *  read as a broken checkbox rather than as two states.
 *
 *  One box now, in both directions. Unchecking a section HIDES it rather than
 *  removing it: it keeps its place and its configuration, so re-checking returns
 *  it to where the author put it. Destructive removal stays on the canvas, where
 *  the author can see what is being removed. */
const CheckBox = (props: { on: boolean; dim?: boolean; locked?: boolean }) => (
  <span style={{
    /* 16, to sit square against the 20px glyph tile beside it. At 15 the two
       controls were a pixel out of centre with each other on every row and the
       column read as slightly loose (operator, 2026-09-14). */
    width: 16, height: 16, borderRadius: 5, flex: "none", display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, lineHeight: 1,
    border: `1.5px solid ${props.on ? (props.locked ? "#9AA1AE" : "#5142C7") : "#C9CDD6"}`,
    background: props.on ? (props.locked ? "#9AA1AE" : "#5142C7") : "#FFFFFF",
    color: "#FFFFFF", opacity: props.dim ? 0.55 : 1,
  }}>{props.on ? "✓" : ""}</span>
);

/* ══ the rail ══════════════════════════════════════════════════════════ */
/** Every section type this page can offer.
 *
 *  A page scoped to one journey type offers that type's sections. A page that
 *  applies to ALL bookings renders for appointments, events AND queues, so it
 *  must be able to carry any of their sections — Guests for the events it
 *  serves, Live queue position for the queue tickets. Collapsing "all" onto a
 *  single representative type (appointment, the old behaviour) silently hid
 *  both from the rail, so an author could not put them on the one page that
 *  needs them most. The canvas still previews ONE concrete type, because a
 *  preview is of a booking and a booking has a kind. */
/** Customer-facing "See what changed" control. Off by operator decision
 *  (2026-09-10); the history itself is untouched behind it. */
const SHOW_HISTORY_BTN = false;

const SCOPE_WORD: Record<CSType, string> = { appointment: "appointments", event: "events", queue: "queue tickets" };

export function csAnyApplies(id: string, types: CSType[], surface: CSSurface = "end"): boolean {
  return types.some((t) => csApplies(id, t, surface));
}

export function CSRail(props: {
  state: CSState;
  type: CSType;
  /** Types this page must serve, when that is more than `type`. Applicability
   *  unions across them; `type` alone still drives the preview. */
  applicableTypes?: CSType[];
  sel: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: CSState) => void;
  /** Which page this rail is authoring — see CSSurface. Defaults to the journey
   *  end page, so the journey designer's call is unchanged. */
  surface?: CSSurface;
  /** Rendered under the section list, above the "sections are not blocks" line.
   *  The booking page's block rail lives here rather than in a second rail. */
  children?: ReactNode;
}) {
  const { state, type, sel, onSelect, onChange } = props;
  const surface = props.surface ?? "end";
  const scope = props.applicableTypes && props.applicableTypes.length ? props.applicableTypes : [type];
  const missing = CONFIRMATION_SECTIONS.filter((d) => csAnyApplies(d.id, scope, surface) && !state.some((r) => r.type === d.id));
  /* Split by applicability rather than filtered at render, so the two lists
     cannot disagree about what "applies" means. An UNKNOWN type is never n/a:
     this build cannot judge a section it has never heard of, and guessing would
     hide it. */
  const onPage = state.filter((r) => !csDef(r.type) || csAnyApplies(r.type, scope, surface));
  const naRows = state.filter((r) => csDef(r.type) && !csAnyApplies(r.type, scope, surface));
  const scopeLabel = scope.length > 1 ? "every booking type" : SCOPE_WORD[scope[0]];
  const [showNa, setShowNa] = useState(false);

  /* Selection is by UID, not by section id: two rows can share a type on a
     document this designer did not write, and a uid is what survives a
     reorder. */
  const row = (r: CSSection) => {
    const d = csDef(r.type);
    const off = !!r.hidden;
    const on = sel === r.uid;
    /* A type the registry does not know is KEPT and shown as itself. Dropping
       it would delete an author's section because this build is older than the
       document. */
    if (!d) return (
      <div key={r.uid} onClick={() => onSelect(on ? null : r.uid)}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 7px", borderRadius: 7, cursor: "pointer",
          border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`, background: on ? "#F6F2FE" : "#FFFFFF", opacity: off ? 0.55 : 1 }}>
        <span onClick={(e) => { e.stopPropagation(); onChange(csToggle(state, r.uid)); }}
          title={off ? "Put it back on the page" : "Take it off the page — it keeps its place"}>
          <CheckBox on={!off} />
        </span>
        <span style={{ width: 20, height: 20, borderRadius: 6, background: "#FDF0E3", color: "#B9770E", fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>?</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: "#697180" }}>{r.type}</span>
        <span title="This build does not know this section. It is kept exactly as saved." style={{ fontSize: 9, color: "#B9770E", flex: "none", ...MONO }}>kept</span>
      </div>
    );
    const na = !csAnyApplies(r.type, scope, surface);
    /* NOT DRAGGABLE, and no remove (operator, 2026-09-02). The rail is a LIST of
       the sections this surface offers — what is on the page and what could be.
       Reordering and removing happen on the canvas, where the author can see
       what moves; two places to reorder meant two gestures for one outcome, and
       a rail drag moved something the author could not see. The eye stays: hide
       is a property of the row, not a rearrangement of the page. */
    return (
      <div
        key={r.uid}
        onClick={() => onSelect(on ? null : r.uid)}
        title={na ? `${d.label} does not render on ${scopeLabel}. It stays on the document until you remove it.` : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 7, cursor: "pointer",
          border: `1px solid ${on ? "#5142C7" : "#E6E8EC"}`,
          background: on ? "#F6F2FE" : "#FFFFFF",
          /* OFF IS NOT UNAVAILABLE (operator, 2026-09-14). A row the author can
             tick must read as tickable: the CHECKBOX says whether the section
             is on the page, and it is the only thing that should. Dimming and
             striking the label said "you cannot have this" about a row whose
             whole purpose is that you can. `n/a` rows keep the dimming, because
             there the claim is true — that section does not render on this
             surface at all. */
          opacity: na ? 0.55 : 1,
        }}>
        {d.locked ? (
          <span title="Locked on — a confirmation page without it reads as a phishing page.">
            <CheckBox on locked />
          </span>
        ) : (
          <span onClick={(e) => { e.stopPropagation(); onChange(csToggle(state, r.uid)); }}
            title={off ? "Put it back on the page" : "Take it off the page — it keeps its place"}>
            <CheckBox on={!off} />
          </span>
        )}
        <span style={{ width: 20, height: 20, borderRadius: 6, background: d.tint, color: d.color, fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{d.glyph}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: on ? "#5142C7" : "#333333" }}>{d.label}</span>
        {na ? <span style={{ fontSize: 9, color: "#9AA1AE", flex: "none", ...MONO }}>n/a</span> : null}
        {d.locked ? <span title="Locked on — it cannot be taken off the page." style={{ fontSize: 9, flex: "none" }}>🔒</span> : null}
      </div>
    );
  };

  /* An OFF-PAGE section, in the SAME list as the ones on the page (operator,
     2026-09-02). It used to be a separate row of chips under the list, which
     read as a different kind of thing and made the author look in two places to
     answer one question — which sections does this page have? Now the list is
     every section the surface offers, and the row itself says whether it is on.
     Dashed, so "available" is never mistaken for "present" — but NOT dimmed
     (operator, 2026-09-14): these are exactly the rows the author is meant to
     reach for, and greying them out told them the opposite. The dashed border
     and the empty checkbox carry the difference on their own. */
  const addRow = (d: ConfirmationSection) => (
    <div
      key={`add-${d.id}`}
      onClick={() => { const { state: next, uid } = csAdd(state, d.id); onChange(next); onSelect(uid); }}
      title={d.help}
      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 8px", borderRadius: 7, cursor: "pointer",
        border: "1px dashed #D9DCE3", background: "#FCFCFD" }}>
      <CheckBox on={false} />
      <span style={{ width: 20, height: 20, borderRadius: 6, background: d.tint, color: d.color, fontSize: 10, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{d.glyph}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 600, color: "#333333" }}>{d.label}</span>
    </div>
  );

  return (
    <>
      <div style={SEC}>Sections</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        {onPage.map((r) => row(r))}
        {missing.map(addRow)}
      </div>
      {/* NON-APPLICABLE ROWS ARE HIDDEN, NOT GREYED (operator, 2026-09-10).
          They used to sit in the list dimmed with an "n/a" tag, on the argument
          that an author who cannot find Guests concludes the designer is broken.
          In practice the opposite happened: a queue-ticket page opened with four
          dead rows an author could tick, configure and never see render, which
          is a worse lie than an absence. "Applies to" is supposed to answer
          "which sections does this page have" — a list that ignores it does not.

          They are hidden rather than DELETED because they are still on the
          document: a page switched from Event to Queue still carries its Guests
          row, and silently dropping an author's section on a dropdown change
          would be destructive. The disclosure keeps them reachable so they can
          be removed deliberately, and states the count so nothing is secret. */}
      {naRows.length ? (
        <div style={{ marginBottom: 12 }}>
          <div onClick={() => setShowNa((v) => !v)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#9AA1AE", cursor: "pointer", fontWeight: 600 }}>
            <span style={{ fontSize: 8 }}>{showNa ? "▾" : "▸"}</span>
            {naRows.length} section{naRows.length === 1 ? "" : "s"} on this page {naRows.length === 1 ? "does" : "do"} not apply to {scopeLabel}
          </div>
          {showNa ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
              {naRows.map((r) => row(r))}
              <div style={{ fontSize: 9.5, lineHeight: 1.5, color: "#B5BAC6", ...MONO }}>
                kept on the document, never rendered · remove them on the canvas
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div style={{ fontSize: 10, lineHeight: 1.5, color: "#B5BAC6", marginBottom: 18, ...MONO }}>
        tick to put a section on the page · untick keeps its place and its settings
      </div>
      {props.children}
    </>
  );
}

/* ══ the canvas ════════════════════════════════════════════════════════ */
const rowLine = (k: string, v: string) => (
  <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11.5 }}>
    <span style={{ color: "#697180", flex: "none" }}>{k}</span><b style={{ textAlign: "right" }}>{v}</b>
  </div>
);
const empty = (text: string) => (
  <div style={{ border: "1px dashed #D1D4DC", borderRadius: 9, padding: "12px 11px", fontSize: 11, lineHeight: 1.5, color: "#697180" }}>{text}</div>
);
const runtimeNote = (text: string) => (
  <div style={{ fontSize: 9.5, color: "#B5BAC6", marginTop: 6, ...MONO }}>{text}</div>
);
/** 16px line glyphs, `currentColor`, drawn from the button's own accent so a
 *  control's icon and label can never disagree about its colour. Deliberately
 *  generic marks: the wallet glyph is a WALLET, not a platform logotype — an
 *  "Add to Apple Wallet" control is Apple's own artwork and a different button
 *  entirely, and must not be approximated with a hand-drawn look-alike. */
const CS_GLYPH: Record<string, string> = {
  calendar: "M4 6.5A1.5 1.5 0 0 1 5.5 5h9A1.5 1.5 0 0 1 16 6.5v8A1.5 1.5 0 0 1 14.5 16h-9A1.5 1.5 0 0 1 4 14.5zM4 8.5h12M7.5 3.5v3M12.5 3.5v3",
  wallet: "M3.5 6.5A1.5 1.5 0 0 1 5 5h9.5A1.5 1.5 0 0 1 16 6.5v7A1.5 1.5 0 0 1 14.5 15H5a1.5 1.5 0 0 1-1.5-1.5zM16 8.5h-2.25a1.5 1.5 0 0 0 0 3H16",
  download: "M10 3.5v8m0 0 3-3m-3 3-3-3M4 13.5v1A1.5 1.5 0 0 0 5.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-1",
  export: "M11.5 4.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h8a1.5 1.5 0 0 0 1.5-1.5V8.5M10 10l5.5-5.5M11.5 4h4v4",
  move: "M5 7.5h9l-2.5-2.5M15 12.5H6l2.5 2.5",
  cancel: "M10 3.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13ZM7.75 7.75l4.5 4.5m0-4.5-4.5 4.5",
  history: "M10 6v4l2.5 1.5M4 10a6 6 0 1 0 1.8-4.3M5 3.5V6h2.5",
};
const Glyph = ({ name }: { name: string }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ flexShrink: 0 }}>
    <path d={CS_GLYPH[name]} />
  </svg>
);

/** A real control. Rendered identically in the designer and on the customer's
 *  page — inert there, because `onClick` is absent, not because it is drawn
 *  differently. The 44px minimum is the touch target: this is a phone surface
 *  far more often than a desktop one. */
const CtlBtn = (props: {
  label: string; accent: string; primary?: boolean; onClick?: () => void;
  busy?: boolean; locked?: boolean; icon?: string;
}) => {
  const inert = !props.onClick || props.locked;
  return (
    <button type="button" disabled={inert}
      onClick={props.onClick ? () => props.onClick!() : undefined}
      style={{
        minHeight: 44, padding: "0 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
        fontFamily: "inherit", cursor: inert ? "default" : "pointer",
        display: "inline-flex", alignItems: "center", gap: 8,
        border: props.primary ? "1px solid transparent" : `1px solid ${props.accent}40`,
        background: props.primary ? props.accent : `${props.accent}0A`,
        color: props.primary ? "#FFFFFF" : props.accent,
        opacity: props.locked ? 0.5 : 1,
      }}>
      {props.icon && !props.busy ? <Glyph name={props.icon} /> : null}
      <span>{props.busy ? "Working…" : props.label}</span>
    </button>
  );
};
const ctlRow = (kids: React.ReactNode) => (
  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>{kids}</div>
);

/** Was #1F8A5B, which measures ~4.35:1 on white — fine for the 11px pills this
 *  section used to draw, under the 4.5:1 floor now that the same colour carries
 *  13px bold button labels. */
/** A takeaway, as a ROW rather than a button.
 *
 *  The downloads section used to be four identical bordered pills that wrapped
 *  three-and-one and gave the eye nothing to sort by. They are not four peer
 *  calls to action — they are a list of things the customer may take away, and
 *  a list is what a list should look like: one card, one row each, the file kind
 *  said out loud, and an arrow on the right that means "this downloads". It
 *  never wraps badly, it reads on a phone, and adding a fifth document costs
 *  nothing.
 *
 *  Inert exactly like `CtlBtn`: no `onClick` (the designer) means no hover, no
 *  pointer, dimmed arrow — the row is still drawn, because the section is
 *  showing what it can carry. */
const DlRow = (props: {
  label: string; sub: string; icon: string; accent: string;
  onClick?: () => void; busy?: boolean; locked?: boolean; last?: boolean;
}) => {
  const [hot, setHot] = useState(false);
  const inert = !props.onClick || props.locked;
  return (
    <button type="button" disabled={inert}
      onClick={props.onClick ? () => props.onClick!() : undefined}
      onMouseEnter={() => setHot(true)} onMouseLeave={() => setHot(false)}
      style={{
        display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
        minHeight: 56, padding: "10px 14px", border: "none",
        borderBottom: props.last ? "none" : "1px solid #EEF0F4",
        background: !inert && hot ? "#F7F8FA" : "#FFFFFF",
        cursor: inert ? "default" : "pointer", fontFamily: "inherit",
        opacity: props.locked ? 0.5 : 1,
      }}>
      <span aria-hidden="true" style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: `${props.accent}14`, color: props.accent,
      }}><Glyph name={props.icon} /></span>
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "#262C38" }}>{props.label}</span>
        <span style={{ fontSize: 11.5, color: "#697180" }}>{props.busy ? "Working…" : props.sub}</span>
      </span>
      <span aria-hidden="true" style={{ color: inert ? "#C4CAD4" : "#697180", display: "inline-flex", flexShrink: 0 }}>
        <Glyph name="download" />
      </span>
    </button>
  );
};
const dlCard = (rows: React.ReactNode) => (
  <div style={{ border: "1px solid #E4E7EE", borderRadius: 12, overflow: "hidden", background: "#FFFFFF" }}>{rows}</div>
);

/* ── control colours ─────────────────────────────────────────────────────
 * Colour carries CONSEQUENCE here, not decoration. The page had exactly two
 * (green for every download, amber for every manage action), which made four
 * equal-weight downloads shout and made Cancel look like Move. Four tokens,
 * each with one job:
 *
 *   CS_QUIET   documents and reads — the customer takes a copy, nothing changes
 *   CS_WALLET  a pass handed to the phone's wallet, a platform artefact
 *   CS_ACTION  the one action that changes the booking (already the page's
 *              accent for "Edit my answers", so the page gains no new colour)
 *   CS_DANGER  withdrawing the booking
 *
 * All four clear 4.5:1 on white at 13px bold; CS_ACTION also clears it as a
 * solid with white text. */
const CS_QUIET = "#3A4254";
const CS_WALLET = "#1D1D1F";
const CS_ACTION = "#5142C7";
const CS_DANGER = "#B42318";

const pillRow = (labels: string[], accent: string) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
    {labels.map((l, index) => <span key={`${l}-${index}`} style={{ border: `1px solid ${accent}`, color: accent, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700 }}>{l}</span>)}
  </div>
);

/** The Booking summary's rows, per journey type — the ONE list the renderer and
 *  the inspector both read, so a row can never be offered as a toggle without
 *  existing, or exist without a toggle. Keys are stable ids, never the labels:
 *  labels differ per type and will be translated. */
export const SUMMARY_ROWS: Record<CSType, { key: string; label: string }[]> = {
  appointment: [
    { key: "service", label: "Service" }, { key: "date", label: "Date" }, { key: "time", label: "Time" },
    { key: "duration", label: "Duration" }, { key: "advisor", label: "With" },
  ],
  event: [
    { key: "event", label: "Event" }, { key: "date", label: "Date" }, { key: "time", label: "Time" },
    { key: "venue", label: "Venue" }, { key: "tickets", label: "Tickets" },
  ],
  queue: [
    { key: "service", label: "Service" }, { key: "ticket", label: "Ticket" }, { key: "where", label: "Where" },
  ],
};

/** The date formats the Booking summary offers. Named shapes, not patterns: an
 *  author picks "04.09.2026", not `dd.MM.yyyy`.
 *
 *  Each carries its OWN locale, which is the whole point of the setting —
 *  "German format" has to stay German for a visitor whose browser is set to
 *  en-US, or it is not a format choice, it is a default. `auto` is the one
 *  exception and the default: it follows the reader. */
export const SUMMARY_DATE_FORMATS: { id: string; label: string; locale: string | undefined; opts: Intl.DateTimeFormatOptions }[] = [
  { id: "auto", label: "Follow the reader’s device", locale: undefined, opts: { day: "numeric", month: "long", year: "numeric" } },
  { id: "dmyLong", label: "4 September 2026", locale: "en-GB", opts: { day: "numeric", month: "long", year: "numeric" } },
  { id: "dmyWeekday", label: "Thursday 4 September 2026", locale: "en-GB", opts: { weekday: "long", day: "numeric", month: "long", year: "numeric" } },
  { id: "deNumeric", label: "04.09.2026 · DE", locale: "de-DE", opts: { day: "2-digit", month: "2-digit", year: "numeric" } },
  { id: "deLong", label: "4. September 2026 · DE", locale: "de-DE", opts: { day: "numeric", month: "long", year: "numeric" } },
  { id: "mdyLong", label: "September 4, 2026 · US", locale: "en-US", opts: { month: "long", day: "numeric", year: "numeric" } },
  { id: "mdyNumeric", label: "09/04/2026 · US", locale: "en-US", opts: { month: "2-digit", day: "2-digit", year: "numeric" } },
  { id: "iso", label: "2026-09-04 · ISO", locale: "en-CA", opts: { year: "numeric", month: "2-digit", day: "2-digit" } },
];

/** Format the booking's start in the chosen shape, in the BOOKING's zone.
 *  Returns null when there is nothing to reformat — no ISO start, no choice, or
 *  a choice of `auto` — and the caller keeps the host's own preformatted line,
 *  which is what every page did before this setting existed. */
function formatSummaryDate(iso: string | undefined, timezone: string | undefined, id: string | undefined): string | null {
  if (!iso || !id || id === "auto") return null;
  const f = SUMMARY_DATE_FORMATS.find((x) => x.id === id);
  if (!f) return null;
  try {
    return new Intl.DateTimeFormat(f.locale, { ...f.opts, ...(timezone ? { timeZone: timezone } : {}) }).format(new Date(iso));
  } catch { return null; }
}

/** Clock shape for the summary's Time row. `auto` follows the reader's device,
 *  which is the default and the only one that varies between readers. The other
 *  two carry a locale that guarantees the shape — an `hour12` flag alone leaves
 *  some locales printing "14:30 PM". */
function formatSummaryTime(startISO: string | undefined, endISO: string | undefined, timezone: string | undefined, id: string | undefined): string | null {
  if (!startISO || !id || id === "auto") return null;
  const locale = id === "h12" ? "en-US" : id === "h24" ? "en-GB" : null;
  if (!locale) return null;
  try {
    const f = new Intl.DateTimeFormat(locale, {
      hour: "2-digit", minute: "2-digit", hour12: id === "h12",
      ...(timezone ? { timeZone: timezone } : {}),
    });
    const from = f.format(new Date(startISO));
    return endISO ? `${from} \u2013 ${f.format(new Date(endISO))}` : from;
  } catch { return null; }
}

function sectionBody(d: ConfirmationSection, type: CSType, b: CSBooking, f: CSPageFields, accent: string, pick: CSAnswersPick | null, st: CSPageState, ctx: CSRenderCtx) {
  /* THE SAME BODIES SERVE THE DESIGNER AND THE CUSTOMER, with two things
     suppressed on the live surface:

     · the grey monospace runtimeNotes. They are notes to the AUTHOR about what
       the section will do at runtime ("the booking reference", "this sample
       booking is virtual"). On a customer's page they are stage directions read
       out loud.
     · the dashed empty states. "No location on the booking yet — a place
       appears here once the journey is attached to one" is authoring guidance;
       to a customer it is a defect notice about their own booking. A section
       with nothing to say renders nothing. */
  const note = (t: string) => (ctx.live ? null : runtimeNote(t));
  const blank = (t: string) => (ctx.live ? null : empty(t));
  switch (d.id) {
    case "brandHeader":
      if (ctx.live && !b.brandName) return null;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: accent, color: "#FFFFFF", fontSize: 12, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{(b.brandName ?? "A").slice(0, 1)}</span>
          <span style={{ fontSize: 13, fontWeight: 800 }}>{b.brandName ?? "Your tenant"}</span>
          <span style={{ flex: 1 }} />
          {note("journey branding")}
        </div>
      );
    case "banner": {
      const o = OUTCOME[st];
      /* The headline override applies to a plain confirmation only. On any other
         outcome the wording IS the outcome — an author's "You're all set" over a
         cancellation would be a lie the page tells for them. */
      const headline = st === "confirmed" || st === "closed" ? (f.bannerTitle || o.title) : o.title;
      /* The sub-line follows the same rule as the headline, for the same
         reason: on a cancellation or a no-show the sentence under the icon IS
         the outcome, and an authored "A confirmation is on its way to your
         email" there would be the page lying on the tenant's behalf. */
      const subline = st === "confirmed" || st === "closed"
        ? (f.bannerSub || o.sub(f.cancelHours || "24"))
        : o.sub(f.cancelHours || "24");
      return (
        <div style={{ textAlign: "center", padding: "4px 0" }}>
          <span style={{ width: 38, height: 38, borderRadius: 999, background: o.bg, color: o.fg, fontSize: 17, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{o.icon}</span>
          <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "-0.01em", marginTop: 8, color: o.fg }}>{headline}</div>
          <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4, lineHeight: 1.55 }}>{subline}</div>
          {st === "confirmed" ? note("wording and colour follow the outcome — confirmed · pending · waitlisted") : null}
        </div>
      );
    }
    case "summary": {
      /* Per-row visibility (operator, 2026-09-14). A tenant that never quotes a
         duration, or does not want the advisor's name on a forwardable page,
         switches that row off rather than losing the whole section.

         VALUE-DRIVEN ABSENCE IS SEPARATE and still wins: a row whose value the
         booking does not carry renders its em dash exactly as before. "We do
         not show this" and "the record does not have this" are different facts
         and stay distinguishable. */
      const dateStr = formatSummaryDate(b.startISO, b.timezone, f.summaryDateFormat) ?? b.dateLine ?? "—";
      const timeStr = formatSummaryTime(b.startISO, b.endISO, b.timezone, f.summaryTimeFormat) ?? b.timeLine ?? "—";
      const value: Record<string, string> = type === "event"
        ? { event: b.eventName ?? "—", date: dateStr, time: timeStr, venue: b.placeName ?? "—", tickets: b.ticketLine ?? "" }
        : type === "appointment"
        ? { service: b.serviceName ?? "—", date: dateStr, time: timeStr, duration: b.durationLine ?? "—", advisor: b.advisor ?? "—" }
        : { service: b.serviceName ?? "—", ticket: b.ticketLine ?? b.reference ?? "—", where: b.placeName ?? "—" };
      const off = f.summaryOff ?? [];
      const labels = f.summaryLabels ?? {};
      const rows = summaryRowsFor(type, f.summaryOrder)
        /* Tickets is the one row that was already conditional on having a
           value — an event without a ticket line never had the row. */
        .filter((r) => (r.key === "tickets" ? !!value.tickets : true))
        .filter((r) => off.indexOf(r.key) < 0);
      if (!rows.length) return null;
      return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{rows.map((r) => rowLine(labels[r.key] || r.label, value[r.key]))}</div>;
    }
    case "place":
      return b.placeName ? (<>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>{b.placeName}</div>
        <div style={{ fontSize: 11.5, color: "#697180", marginTop: 2 }}>{b.address || "no address on the location"}</div>
        {b.virtual ? note("this sample booking is virtual — Place is suppressed at runtime") : null}
      </>) : blank("No location on the booking yet — a place appears here once the journey is attached to one.");
    case "meeting": {
      /* A join link is a LIVE capability, so it follows the booking's outcome
         and not just its channel. Offering "Join the meeting" on a cancelled or
         finished booking sends the customer to a room nobody is in — found on
         the seeded 1003/1004 previews, where it rendered under a banner that had
         just said the booking was over.
         The designer still shows the row for every state: it is showing what the
         section can carry, and an author needs to see it to place it. */
      /* LOCAL PATCH (platform side, 2026-09-08, JAI-1292): on the live surface this row is
         about the booking's REAL meeting, or nothing. `b.virtual` is set only from the
         session's virtual location, so an in-person or phone booking renders nothing here
         (CD's arm drew a 'Join the meeting' label with no link on every appointment, and
         the "no longer active" copy on every cancelled one). For a virtual booking: the
         cancelled/past copy, else the real `join_url` (null until the server reveals it)
         plus the passcode, else nothing. The designer preview keeps CD's arm as it was. */
      if (ctx.live) {
        if (!b.virtual) return null;
        if (st === "cancelled" || st === "past") {
          return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>The meeting link is no longer active.</div>;
        }
        if (!b.joinUrl) return null;
        return (<>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EDE5FE", color: "#5142C7", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>▷</span>
            <a href={b.joinUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: accent }}>Join the meeting</a>
          </div>
          {b.passcode ? <div style={{ fontSize: 11.5, color: "#697180", marginTop: 4 }}>Passcode: {b.passcode}</div> : null}
        </>);
      }
      return (<>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 26, height: 26, borderRadius: 7, background: "#EDE5FE", color: "#5142C7", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>▷</span>
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>Join the meeting</span>
        </div>
        {note(st === "cancelled" || st === "past" ? "withdrawn at runtime once the booking is over or cancelled" : "renders only for a phone or video booking")}
      </>);
    }
    case "guests":
      return b.guests && b.guests.length
        ? <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>{b.guests.map((g) => <span key={g} style={{ border: "1px solid #E7A4B8", background: "#FDF3F6", color: "#D61A55", borderRadius: 999, padding: "4px 10px", fontSize: 11, fontWeight: 700 }}>{g}</span>)}</div>
        : blank("Guests captured at registration list here — this event allows none yet (Setup · Registration).");
    case "answers": {
      /* The switcher is the Contact page's control, in the host's hands: same
         rows, same per-page memory. It is rendered even with a pick already
         made, so changing service is one click rather than a deselect. */
      const chips = pick && pick.options.length ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A92A0" }}>Preview as</span>
          {pick.options.map((o) => {
            const on = o.id === pick.selected;
            return (
              <button key={o.id} onClick={(e) => { e.stopPropagation(); pick.onPick(on ? "" : o.id); }}
                style={{ padding: "3px 9px", borderRadius: 999, cursor: "pointer", fontFamily: FONT, fontSize: 10.5, fontWeight: 700, border: `1px solid ${on ? "#5142C7" : "#D9DCE3"}`, background: on ? "#5142C7" : "#FFFFFF", color: on ? "#FFFFFF" : "#4B5563", whiteSpace: "nowrap" }}>
                {o.name}
              </button>
            );
          })}
        </div>
      ) : null;
      const body = b.answers && b.answers.length ? (<>
        {b.answersBadge ? <div style={{ display: "inline-block", background: "#FFF7E8", color: "#8A5A08", border: "1px solid #F3D9A4", borderRadius: 999, padding: "2px 9px", fontSize: 9.5, fontWeight: 800, marginBottom: 7 }}>{b.answersBadge}</div> : null}
        {/* STACKED, NOT SPREAD (operator, 2026-09-14). Answers used to reuse
            `rowLine`, whose label-left / value-right split is right for the
            booking summary — five short pairs, values that line up in a column.
            A question is a sentence and its answer is a sentence: pushed to
            opposite edges of a wide card, the eye had to travel the full width
            to join two things that belong together, and neither column lined up
            with anything.

            Question bold, answer directly under it, unbolded. Pairs separated
            by more space than the two lines inside a pair, so the grouping is
            in the spacing rather than in the alignment. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {b.answers.map((a) => (
            <div key={a.label} style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: "#333333" }}>{a.label}</span>
              <span style={{ fontSize: 11.5, color: "#4B5563", lineHeight: 1.5 }}>{a.value}</span>
            </div>
          ))}
        </div>
        {type === "event" ? null : note("the customer sees the questions their own service asked")}
      </>) : blank(
        type === "event"
          ? "The event's booking-question answers appear here — attach a group in Setup · Registration."
          : !pick || !pick.options.length
          ? "The questions came from the SERVICE the customer picked, so this page cannot show fixed content — each service asks its own. This journey offers none yet; they are set per service in Setup."
          : pick.selected
          ? "This service asks no questions — a booking through it reaches this page with nothing to list."
          : "Each service asks its own questions, so pick one above to preview what a customer who booked it would see here.");
      /* Edit answers lives HERE (2026-09-07), with the answers it edits, rather
         than in a card of its own. Policy-shaped like everything else: absent
         when the server does not allow it, and nothing at all until the session
         has said. It also only appears where there are answers to edit — an
         Edit button over an empty state edits nothing. */
      /* GOVERNED BY THE SERVER FLAG, NOT BY US (operator ruling, 2026-09-11,
         reversing our 09-10 withdrawal).

         We had gated this behind an `adminMode` nothing set, which withdrew it
         from the customer everywhere. The API line's objection was correct and
         is worth keeping written down: `edit_answers` has a real endpoint with
         its own policy checks and verification, so hiding the button while the
         endpoint stays open is the APPEARANCE of withdrawal, not withdrawal —
         anyone who can reach the page can still reach the operation. A UI-only
         withdrawal is strictly worse than either honouring the flag or changing
         backend authorization, because it reads as a control and is not one.

         Editing your own answers on your own booking is also low-risk in the
         sense the manage-link ruling means: it moves no money, no time and no
         identity. So `actions.edit_answers.allowed` decides, the same as every
         other control on this page. */
      const canEdit = ctx.live
        ? !!(ctx.wiring?.policyKnown && ctx.wiring.canEditAnswers)
        : true;
      const editBtn = canEdit && b.answers && b.answers.length
        ? <div style={{ marginTop: 10 }}>{ctlRow([
            <CtlBtn key="ed" label="Edit my answers" accent="#5142C7"
              onClick={ctx.live ? ctx.wiring?.onEditAnswers : undefined}
              busy={ctx.live && ctx.wiring?.busy === "answers"}
              locked={!!(ctx.live && ctx.wiring?.busy && ctx.wiring.busy !== "answers")} />,
          ])}</div>
        : null;
      /* LOCAL PATCH (platform side, 2026-09-09): an empty fragment is not
         "nothing" to the canvas, which decides on `null`. */
      if (chips === null && body === null && editBtn === null) return null;
      return <>{chips}{body}{editBtn}</>;
    }
    case "checkinCode": {
      /* WHAT IT ENCODES IS AUTHORED (2026-09-14), because two scanners need
         different payloads:
           - `reference` (the saved key, kept for stored pages) is THE BOOKING'S
             RECORD ID, `bookingId`, for every journey type (CD, 2026-09-18).
             My Day scans it and resolves the record; the human reference stays
             printed on its own line.
           - `manageUrl` is the customer's manage link, for a phone camera, for
             every type. It is access-bearing: the booking id in it is the credential.

         NO FALLBACK AND NO SUBSTITUTION ON A LIVE PAGE. A booking with no
         record id draws NO code for the reference target — never the human
         reference, never a sample — and the link target without `manageUrl`
         draws nothing. Samples exist in the designer only, labelled as
         samples so they cannot pass for a real credential. The reference sample
         must not collide with a real reference (a mock booking once genuinely
         carried the old sample string). */
      // The human reference: printed beside the code, and the encoded value for event/queue.
      const ref = b.reference ?? (ctx.live ? null : (type === "queue" ? "A\u2011SAMPLE" : "BK\u2011SAMPLE"));
      const target: CheckinCodeTarget = f.checkinCodeTarget === "manageUrl" ? "manageUrl" : "reference";
      const isAppointment = type === "appointment";
      /* THE RECORD ID, FOR ALL THREE TYPES (CD, 2026-09-18).

         It was per-type and neither branch encoded something a scanner
         resolves: an appointment encoded `checkInCode`, the 8-character
         reception shorthand — which the ANONYMOUS confirmation lane never
         carries by design, so the appointment QR on this page drew nothing at
         all — and an event or queue encoded the human `reference`, which is a
         number to read out, not an identifier. My Day scans for the record, so
         the record id is what the code holds, and `bookingId` is required on
         this response rather than optional like the other two.

         The saved key stays `reference`, so stored pages keep their setting and
         no backend enum moves. The human reference is still printed beside it
         (`ref` below), and `manageUrl` is untouched. Still no substitution on a
         live page: no id, no code. */
      const recordId = typeof b.bookingId === "string" && b.bookingId.trim() !== "" ? b.bookingId : null;
      const referenceTargetValue = recordId ?? (ctx.live ? null : "SAMPLE-BOOKING-ID");
      const manageHref = b.manageUrl ?? (ctx.live ? null : "https://manage.example.org/BK\u2011SAMPLE");
      const value = target === "manageUrl" ? manageHref : referenceTargetValue;
      if (!value) return null;
      const isUrl = target === "manageUrl";
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <QrCode value={value} size={104} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#333333" }}>{isUrl ? "Scan to open your booking" : "Show this at reception"}</span>
            {/* Only an appointment's real check-in code promises check-in
                (2026-09-15); an event or queue code says it holds the reference,
                and a designer sample says it is a sample. */}
            <span style={{ display: "block", fontSize: 11.5, color: "#697180", marginTop: 3, lineHeight: 1.55 }}>{isUrl
              ? "It opens this page on your phone. Anyone who scans it can manage the booking."
              : recordId
                ? (isAppointment
                  ? "Reception scans it to check you in. Your booking reference is below."
                  : "Staff scan it to find your booking. Your reference is below.")
                : "Sample code. A real booking shows its own here."}</span>
            {ref ? <span style={{ display: "block", fontSize: 12, fontWeight: 800, letterSpacing: "0.06em", marginTop: 6, ...MONO }}>{ref}</span> : null}
          </span>
        </div>
      );
    }
    case "reference": {
      /* Same rule as the check-in code: a made-up reference on a live page is a
         number a customer will read out to staff. Sample in the designer only. */
      const rf = b.reference ?? (ctx.live ? null : (type === "queue" ? "A‑SAMPLE" : "BK‑SAMPLE"));
      if (!rf) return null;
      return (<>
        <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: "0.06em", ...MONO }}>{rf}</div>
        {note(type === "queue" ? "the queue ticket number" : "the booking reference")}
      </>);
    }
    case "downloads": {
      /* REAL BUTTONS since 2026-09-07 (operator: the customer sees exactly the
         authored page). This section IS the download affordance now — the
         platform's action card that used to carry them is gone from the
         customer surface, so a section left off the page means the documents
         are not offered. That is the point of the ruling.

         Every one is a SERVER-OWNED FLAG, rendered and never probed: a button
         that discovers it is unavailable by earning a 403 has turned a refusal
         into an affordance. The designer has no flags and shows all of them,
         because it is showing what the section can carry. */
      const w = ctx.wiring;

      /* ONE PATH, for the same reason as the manage section. The designer branch
         used to return all four buttons unconditionally, so previewing
         Cancelled showed "Add to calendar" while the live page withdrew it — the
         author was previewing a state the customer never gets. State gating now
         applies to both surfaces; only the handlers and the server flags differ.
         Nothing until the session says what is permitted: every control here
         needs a handler the session owns, so before it lands the honest
         rendering is no section rather than dead buttons. */
      if (ctx.live && !w?.policyKnown) return null;
      const may = (flag: boolean | undefined) => (ctx.live ? flag === true : true);
      const busyIs = (k: string) => ctx.live && w?.busy === k;
      const lockedFor = (k: string) => !!(ctx.live && w?.busy && w.busy !== k);

      /* A cancelled or finished booking is not addable to a calendar and its
         pass is spent. The PDF and the export stay: the record still happened,
         and that is what those documents are. */
      const spent = st === "past" || st === "cancelled";
      /* THE AUTHOR CAN ONLY TAKE AWAY (2026-09-14). Every gate above this line
         is the server's answer to "does this exist"; `downloadsOff` is the
         tenant's answer to "do we offer it". Applied last and never the other
         way round, so switching an item on can never conjure one the server
         does not have. */
      const withheld = (id: string) => (f.downloadsOff ?? []).indexOf(id) > -1;
      const rows = [
        !spent && !withheld("ics") ? { key: "ics", label: "Add to calendar", sub: "Calendar file (.ics)", icon: "calendar", on: ctx.live ? w?.onIcs : undefined, k: "ics" } : null,
        /* TWO flags, one question each, and the row needs both:
             `canPdf`     (session) — may THIS BOOKING use it
             `summaryPdf` (public)  — does the OPERATION exist on this tenant
           Visibility and handler both come from `canPdf`, so they cannot
           disagree; `summaryPdf` is the separate precondition. There is no
           `ctx.live ||` bypass in front of it — that short-circuited the
           precondition on the live surface, which left a tenant with the
           operation switched off drawing a working control that could only earn
           a refusal, and made the read dead code on both surfaces. The designer
           passes no `actions`, so `undefined !== false` keeps the row visible
           there, which is what showing-what-it-can-carry means. */
        may(w?.canPdf) && ctx.actions?.summaryPdf !== false && !withheld("pdf")
          ? { key: "pdf", label: "Download PDF", sub: "Your booking summary", icon: "download", on: ctx.live ? w?.onPdf : undefined, k: "pdf" } : null,
        may(w?.dataExport) && !withheld("export")
          ? { key: "exp", label: "Export my data", sub: "Everything we hold about this booking", icon: "export", on: ctx.live ? w?.onExport : undefined, k: "export" } : null,
      ].filter(Boolean) as { key: string; label: string; sub: string; icon: string; on?: () => void; k: string }[];

      /* The wallet pass has no operation behind it yet, so on the live surface
         it is not drawn at all rather than drawn dead — the server flag is
         false and stays false until one exists. The designer shows it because
         it is showing what the section can carry; when the flag flips nothing
         else changes.

         It stands APART from the list, not in it. Apple's badge is a control in
         its own right with its own size and clear space — squeezing it into a
         row, or worse redrawing it as one, is exactly what the badge rules
         forbid. */
      const pass = !spent && !withheld("wallet") && (ctx.live ? ctx.actions?.walletPass === true : true)
        ? <WalletPassBadge href={ctx.live ? w?.walletPassUrl : undefined}
            note="Keep the pass on your phone for check-in."
            fallback={<CtlBtn label="Wallet pass" icon="wallet" accent={CS_WALLET} onClick={undefined} />} />
        : null;

      if (!rows.length && !pass) return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>No documents are available for this booking.</div>;
      return (<>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 12 }}>
          {rows.length ? <div style={{ alignSelf: "stretch" }}>{dlCard(rows.map((r, i) => (
            <DlRow key={r.key} label={r.label} sub={r.sub} icon={r.icon} accent={CS_QUIET}
              onClick={r.on} busy={busyIs(r.k)} locked={lockedFor(r.k)} last={i === rows.length - 1} />
          )))}</div> : null}
          {pass}
        </div>
        {note("each follows a server flag — wallet pass is not built yet, so it renders only where the flag says so")}
      </>);
    }
    case "manage": {
      /* REAL Move and Cancel since 2026-09-07. They open the manage surface's
         own dialogs and inherit its step-up: any call that comes back
         `403 verificationRequired` parks on the verification prompt and retries
         after the code, which is why nothing here asks for a code itself.

         POLICY IS ABSENT-MEANS-NOT-OFFERED. A button the server would refuse is
         not drawn disabled with an explanation — it is not drawn. The one
         exception is the closed window, because "you could have changed this
         until Tuesday" is something the customer needs told. */
      const w = ctx.wiring;

      /* ONE PATH, not a live branch and a designer branch (2026-09-07).
         Splitting them is how they disagreed: the live path added See what
         changed to the gated states and the designer path returned the reason
         alone, so an author previewing Cancelled saw no history affordance
         while the real customer got a button. The whole point of the shared
         canvas is that these two cannot diverge, so the only difference below
         is whether a handler exists — which is already how every control here
         stays inert in the designer. */
      if (ctx.live && !w?.policyKnown) {
        /* Policy not arrived. "Please contact the branch" is the answer for a
           tenant that does not offer self-service, NOT for an answer still in
           flight, so nothing renders until it lands. */
        return null;
      }
      /* In the designer every affordance the section CAN carry is shown — it is
         showing what the section is, not what one booking permits. */
      const may = (flag: boolean | undefined) => (ctx.live ? flag === true : true);
      const busyIs = (k: string) => ctx.live && w?.busy === k;
      const lockedFor = (k: string) => !!(ctx.live && w?.busy && w.busy !== k);

      /* See what changed survives every gate Move and Cancel do not. A
         cancelled, past or window-closed booking is exactly when a customer
         wants to read what happened to it — it is a READ, so no window bounds
         it. */
      /* WITHDRAWN, not deleted (operator, 2026-09-10). The rebook history is
         real and stays wired — `canHistory`/`onHistory` still travel, and
         ManagePage still opens the dialog — but the customer-facing control is
         not offered yet: "See what changed" beside Move and Cancel reads as a
         third action of the same weight, and on a booking that has never
         changed it opens an empty log. Flip SHOW_HISTORY_BTN to bring it back;
         nothing else has to be rebuilt. */
      const histBtn = SHOW_HISTORY_BTN && may(w?.canHistory)
        ? <CtlBtn key="hist" label="See what changed" icon="history" accent={CS_QUIET} onClick={ctx.live ? w?.onHistory : undefined} busy={busyIs("history")} locked={lockedFor("history")} />
        : null;

      /* TWO WINDOWS, NOT ONE (operator, 2026-09-14). Moving a booking and
         cancelling it are different costs to the branch, so they close at
         different times — typically "move up to 4 hours before, cancel up to
         24". `rebookHours` left empty means the tenant runs ONE window and Move
         follows Cancel, which is what every page authored before today did:
         the field is additive, and an unset field cannot change an existing
         page's wording. */
      const cancelH = f.cancelHours || "24";
      const rebookH = f.rebookHours || cancelH;
      const oneWindow = rebookH === cancelH;
      /* THE SERVER'S DEADLINE WINS WHERE THERE IS ONE (platform 2026-09-14).
         `closes_at` is computed from the same windows this page authors, but by
         the worker that will refuse the call — so it accounts for the booking's
         own start time, which an hours figure cannot. The authored hours stay
         as the fallback for the designer, and for a tenant the field has not
         reached yet. */
      const moveBy = w?.rescheduleClosesLabel;
      const cancelBy = w?.cancelClosesLabel;
      const serverDeadlines = ctx.live && (moveBy || cancelBy);
      const reason = st === "cancelled" ? "This booking is already cancelled."
        : st === "past" ? "Past bookings cannot be changed."
        : st === "closed" || (ctx.live && w?.windowClosed)
          ? (serverDeadlines
            ? `Changes are closed. Call the branch to move or cancel.`
            : oneWindow
              ? `Changes closed ${cancelH} hours before the start time. Call the branch to move or cancel.`
              : `Moving closed ${rebookH} hours before the start time and cancelling ${cancelH} hours before. Call the branch to move or cancel.`)
          : "";
      if (reason) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>{reason}</div>
            {histBtn ? ctlRow([histBtn]) : null}
          </div>
        );
      }

      const kids = [
        may(w?.canMove) ? <CtlBtn key="mv" primary label="Move this booking" icon="move" accent={CS_ACTION} onClick={ctx.live ? w?.onMove : undefined} busy={busyIs("move")} locked={lockedFor("move")} /> : null,
        may(w?.canCancel) ? <CtlBtn key="cx" label="Cancel booking" icon="cancel" accent={CS_DANGER} onClick={ctx.live ? w?.onCancel : undefined} busy={busyIs("cancel")} locked={lockedFor("cancel")} /> : null,
        histBtn,
      ].filter(Boolean);
      /* Nothing permitted and the window still open means the tenant does not
         offer self-service on this booking. Say that, rather than printing a
         window that turns out to buy nothing. */
      if (!kids.length) return <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>To move or cancel this booking, please contact the branch.</div>;
      return (<>
        {ctlRow(kids)}
        {serverDeadlines
          ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6, marginTop: 8 }}>
              {moveBy && cancelBy && moveBy !== cancelBy
                ? `Move until ${moveBy} · cancel until ${cancelBy}.`
                : `You can change this until ${moveBy || cancelBy}.`}
            </div>
          : note(oneWindow
            ? `free until ${cancelH} hours before — after that Move and Cancel are withdrawn and the reason replaces them`
            : `Move closes ${rebookH} hours before, Cancel ${cancelH} — each is withdrawn at its own window and the reason replaces them`)}
      </>);
    }
    case "yourDetails": {
      /* The customer's own name and email (2026-09-07). This section exists
         because the platform's "Your details" card was removed from the
         customer surface — so if an admin does not place it, these details are
         not shown, which is the ruling working as intended.

         MASKING IS READ, NEVER INFERRED. `w.masked` comes from the response's
         manifest; the value on the wire is ALREADY masked when it is on, so
         nothing here does the masking — doing it twice is how the two
         disagree. Reveal runs the manage surface's own step-up. */
      const w = ctx.wiring;
      const blue = "#2D60EB";
      const lines: { k: string; v: string }[] = [];
      if (b.customerName) lines.push({ k: "Name", v: b.customerName });
      if (b.customerEmail) lines.push({ k: "Email", v: b.customerEmail });
      if (!lines.length) {
        return ctx.live
          ? <div style={{ fontSize: 11.5, color: "#697180", lineHeight: 1.6 }}>We do not hold personal details against this booking.</div>
          : blank("The customer's name and email render here, from the booking.");
      }
      return (<>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {lines.map((l) => rowLine(l.k, l.v))}
        </div>
        {w?.masked ? (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.55, marginBottom: 8 }}>
              Part of your details is hidden while this link is unverified.
            </div>
            {w.canReveal
              ? ctlRow([<CtlBtn key="rv" label="Show my details" accent={blue} onClick={w.onReveal} busy={w.busy === "reveal"} locked={!!w.busy && w.busy !== "reveal"} />])
              : null}
          </div>
        ) : null}
        {ctx.live ? null : note("masked on a tenant with masking on, with a reveal step — in the clear on the common tenant, where masking defaults off")}
      </>);
    }
    case "another": {
      /* LOCAL PATCH (platform side, 2026-09-08, JAI-1292): on the live surface the control rides `wiring.onBookAnother` — the
         session's `book_another` action, the same door the deleted ActionsCard used —
         and is NOT drawn when that door is absent, per the rule the downloads section
         states for the wallet pass: never drawn dead. The designer keeps the label. */
      if (ctx.live) {
        const go = ctx.wiring?.onBookAnother;
        return go ? ctlRow([<CtlBtn key="ba" label="Book another" accent={accent} onClick={go} />]) : null;
      }
      return <span style={{ fontSize: 12, fontWeight: 700, color: accent, textDecoration: "underline" }}>Book another</span>;
    }
    case "promo":
      return b.promo ? (
        <div style={{ border: "1px solid #DCCCFC", background: "#FAF8FF", borderRadius: 10, padding: "10px 12px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 700 }}>{b.promo.title}</div>
          {b.promo.sub ? <div style={{ fontSize: 11, color: "#697180", marginTop: 2 }}>{b.promo.sub}</div> : null}
        </div>
      ) : blank("One promoted service or event shows here — pick it in the inspector.");
    case "footer":
      if (ctx.live && !b.footerLines?.length && !f.note) return null;
      return (<>
        <div style={{ fontSize: 10.5, lineHeight: 1.6, color: "#697180" }}>
          {(b.footerLines ?? (ctx.live ? [] : ["Tenant address", "Contact · Privacy · Terms"])).map((l) => <div key={l}>{l}</div>)}
        </div>
        {f.note ? <div style={{ fontSize: 11, color: "#333333", marginTop: 6, lineHeight: 1.5 }}>{f.note}</div> : null}
      </>);
    /* Merged from the booking confirmation page's registry, 2026-09-02. */
    case "prepare":
      return (b.prepare && b.prepare.length) ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {b.prepare.map((l) => (
            <div key={l} style={{ display: "flex", gap: 7, fontSize: 11.5, color: "#4B5563" }}>
              <span style={{ color: accent, flex: "none" }}>☑</span><span>{l}</span>
            </div>
          ))}
        </div>
      ) : blank("What to bring — ID, documents, what to wear. Add the lines in the inspector.");
    case "queuePos":
      return (<>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", ...MONO }}>{b.queuePos ?? "3"}</span>
          <span style={{ fontSize: 11.5, color: "#697180" }}>in line · about {b.queueWait ?? "12 min"} to wait</span>
        </div>
        {note("live — this counts down while the page is open")}
      </>);
    case "survey":
      /* `afterTheFact` already keeps this off the journey end page. The state is
         the second half of the same rule: on the linked page it still only means
         anything once the booking has actually happened. */
      return st === "past" ? (<>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{f.surveyPrompt || "How did it go?"}</div>
        {pillRow(["★", "★", "★", "★", "★"], "#B9770E")}
      </>) : blank("Invites a response once the booking has passed — switch the state to Past to preview it.");
    default:
      return null;
  }
}

/**
 * What the page can actually DO, and who does it (2026-09-07, operator ruling:
 * the customer sees exactly the authored page, nothing extra).
 *
 * The platform's own action card and details card are gone from the customer
 * surface, so Move, Cancel, the documents and the customer's details are now
 * the authored sections' own controls. That makes the canvas render real
 * buttons — which is also why this is ONE object passed in rather than logic
 * inside the canvas: the canvas knows how a section LOOKS, and the surface that
 * owns the session knows what is permitted and what pressing it does.
 *
 * ABSENT HANDLERS ARE THE DESIGNER. With no wiring the sections render the same
 * controls, inert, so the author previews exactly what ships instead of a line
 * of prose standing in for a button. That is the whole point of one canvas.
 *
 * POLICY IS THE SERVER'S. `canMove`/`canCancel` come from the manage view's
 * `actions` and are ABSENT-means-not-offered, never a disabled button with an
 * explanation — a cancellation window reimplemented here would drift from the
 * server's. `windowClosed` is the one exception, because "you could have done
 * this until Tuesday" is information the customer needs.
 */
export interface CSWiring {
  /** Has the SERVER's policy arrived yet?
   *
   *  This is the difference between "not offered" and "not known yet", and
   *  collapsing the two put false copy on the customer's page: the authored page
   *  renders BEFORE the session exchange (that is the whole point of the public
   *  lane), so during the exchange `canMove`/`canCancel` are merely absent — and
   *  absent was being read as the server withholding them. The customer was told
   *  to phone the branch, for a second or two, and then handed working Move and
   *  Cancel buttons. Downloads had the same shape from the other side: the PDF
   *  flag is on the PUBLIC response, so the button was drawn, but its handler
   *  comes from the session, so it was drawn disabled — the exact thing this
   *  change says it will never do.
   *
   *  False means POLICY UNKNOWN: the sections that depend on it render nothing
   *  and appear when the answer does. A section arriving a moment late is
   *  honest; a wrong instruction is not. */
  policyKnown?: boolean;
  canMove?: boolean;
  canCancel?: boolean;
  /** Edit answers, on the ANSWERS section (2026-09-07). It had no home when the
   *  platform's action card was removed, and a card for the two leftovers was
   *  the last thing on the page the admin had not placed. Folded into the
   *  section it belongs to instead of a fourteenth registry entry: the control
   *  that edits the answers lives with the answers. */
  canEditAnswers?: boolean;
  /** May THIS booking's summary PDF be downloaded — the session's own
   *  `actions.summary_pdf.allowed`.
   *
   *  Separate from `CSRenderCtx.actions.summaryPdf`, and the distinction is the
   *  whole point: the public flag says whether the OPERATION EXISTS (true for
   *  every tenant today), the session flag says whether THIS BOOKING may use it.
   *  Taking visibility from the first and the handler from the second drew a
   *  greyed-out button for any booking whose policy withheld the PDF — the same
   *  disabled-button defect this change twice says it will never ship. One flag
   *  owns both now. */
  canPdf?: boolean;
  /** See what changed — the rebook history — on the MANAGE section, beside the
   *  two actions whose history it records. */
  canHistory?: boolean;
  /** The change window has passed. Renders the reason in place of the buttons. */
  windowClosed?: boolean;
  dataExport?: boolean;
  /** Reveal is available: masking is on AND a step-up channel exists. The
   *  settings validator refuses masking with no channels, so these travel
   *  together and a masked-with-no-way-out state cannot be reached. */
  canReveal?: boolean;
  masked?: boolean;
  onMove?: () => void;
  onCancel?: () => void;
  onEditAnswers?: () => void;
  onHistory?: () => void;
  onIcs?: () => void;
  onPdf?: () => void;
  onExport?: () => void;
  /** The SERVER's own deadlines, already formatted in the booking's zone by the
   *  manage surface (`actions.reschedule.closes_at` / `actions.cancel.closes_at`,
   *  platform 2026-09-14). Present = print these; the authored hours are what
   *  the page falls back to, not what it prefers. A deadline the server
   *  computed is the only one it will actually enforce. */
  rescheduleClosesLabel?: string;
  cancelClosesLabel?: string;
  /** The `.pkpass` download for THIS booking, when the tenant signs passes and
   *  the session minted one. Absent = no badge; never an empty string, and
   *  never a badge that goes nowhere on the live surface. */
  walletPassUrl?: string;
  onReveal?: () => void;
  /** LOCAL PATCH (platform side, 2026-09-08, JAI-1292): the session's `book_another` target, when the tenant offers one. */
  onBookAnother?: () => void;
  /** Key of the control mid-flight, so it can say so and the rest can lock. */
  busy?: string | null;
}

export interface CSRenderCtx {
  /** True on the CUSTOMER surface (`src/manage`). Default false = the designer. */
  live?: boolean;
  /** Server-owned download flags. Absent = the designer, which shows both. */
  actions?: { summaryPdf: boolean; walletPass: boolean };
  /** What the controls may do. Absent = the designer's inert preview. */
  wiring?: CSWiring;
}

export function CSCanvas(props: {
  state: CSState;
  type: CSType;
  /** Types this page must serve, when that is more than `type`. Applicability
   *  unions across them; `type` alone still drives the preview. */
  applicableTypes?: CSType[];
  booking: CSBooking;
  fields: CSPageFields;
  sel: string | null;
  onSelect: (id: string | null) => void;
  accent?: string;
  /** Only the Answers section reads it, and only where the type has services
   *  to choose between. */
  answersPick?: CSAnswersPick | null;
  surface?: CSSurface;
  /** What happened to the booking. Defaults to `confirmed`, which is the only
   *  outcome a journey end page can have. */
  pageState?: CSPageState;
  /** True on the CUSTOMER surface (`src/manage`): authoring annotations and
   *  authoring empty states are suppressed, and the download pills follow the
   *  server's flags. Default false = the designer. */
  live?: boolean;
  actions?: { summaryPdf: boolean; walletPass: boolean };
  /** Supplied to get the per-section controls on the canvas — move up, move
   *  down, remove — the same three the block canvas has. Omitted, the canvas is
   *  read-only and no controls render. */
  onChange?: (next: CSState) => void;
  /** What the page's controls may do, and what pressing them runs. Absent = the
   *  designer's inert preview of the same controls.
   *
   *  Declared here as well as on `CSRenderCtx` because they are two different
   *  types: the ctx is what `sectionBody` receives, this is what a CALLER passes.
   *  It was added to the ctx and missed here, and the preview bundles without
   *  typechecking, so it built locally and failed the kit's strict config
   *  (TS2339 in `CSCanvas`, TS2322 at every call site). */
  wiring?: CSWiring;
  /** The page's authored blocks (`content[]` on the document). Rendered after
   *  the sections, read-only; the designer previews them on its own block
   *  canvas and does not pass them here. LOCAL PATCH (platform side, 2026-09-09). */
  /** `at` is the block's authored slot among the sections: absent = after the last
   *  section, negative clamps to 0, beyond the section count clamps to the end, and
   *  blocks sharing a slot keep their order. */
  content?: ReadonlyArray<{ uid: string; type: string; text?: unknown; href?: unknown; at?: number }>;
}) {
  const { state, type, booking, fields, sel, onSelect } = props;
  const ctx: CSRenderCtx = { live: props.live === true, actions: props.actions, wiring: props.wiring };
  const surface = props.surface ?? "end";
  const accent = props.accent ?? "#14131F";
  const [hover, setHover] = useState<string | null>(null);
  const st = props.pageState ?? "confirmed";
  /* Hidden, unknown and not-applicable rows all stay on the page and none of
     them render. Unknown is ignored rather than rejected — the same rule the
     document's open interior follows.
     An expired link additionally renders only the three sections that carry no
     booking data: the link has just been judged not to identify a booking. */
  const shown = state.filter((r) => !r.hidden && csDef(r.type) && csApplies(r.type, type, surface)
    && (st !== "expired" || EXPIRED_ONLY.indexOf(r.type) > -1));
  /* Nudge moves within the WHOLE document, not within the shown subset: an
     index from a filtered list would jump a section past every hidden one
     between it and its neighbour. */
  const nudge = (uid: string, delta: number) => {
    if (!props.onChange) return;
    const from = state.findIndex((r) => r.uid === uid);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= state.length) return;
    props.onChange(csMove(state, from, to));
  };
  /* ONE PAGE, TWO ARRAYS (operator, 2026-09-14). Blocks used to render after
     every section, so a heading could never sit between two of them. Each block
     now carries `at` — how many section rows precede it — and the page is walked
     slot by slot: the blocks for a slot, then that slot's row. Absent or
     out-of-range `at` clamps to the end, which is exactly where every block
     authored before today already was, so nothing moves on its own. */
  const blocks = props.content ?? [];
  const slotOf = (b: { at?: unknown }) => {
    const raw = typeof b.at === "number" && Number.isFinite(b.at) ? Math.floor(b.at) : state.length;
    return Math.max(0, Math.min(raw, state.length));
  };
  const slots = Array.from({ length: state.length + 1 }, (_, index) => ({
    index,
    row: index < state.length ? state[index] ?? null : null,
    blocks: blocks.filter((b) => slotOf(b) === index),
  }));

  const renderRow = (r: CSState[number], i: number) => {
    if (shown.indexOf(r) < 0) return null;
    const d = csDef(r.type)!;
    const on = sel === r.uid;
    /* × on hover so a section can go without being selected first; the
           arrows only when selected, which keeps them off screen while still
           leaving a path to reorder that is not a drag. Same rule as the block
           canvas, so one gesture serves both. */
        const showCtl = !!props.onChange && (on || hover === r.uid) && !d.locked;
        /* LOCAL PATCH (platform side, 2026-09-09): a section with nothing to say
           renders nothing — including its frame. `sectionBody` already answers
           `null` on the live surface for a section whose data is absent (Book
           another with no target, Answers with none, Meeting type on an
           in-person booking, ...); drawing the bordered wrapper around that
           null put an empty box on every customer page for each of them. The
           designer arms return their placeholder text, so nothing changes there. */
        const body = sectionBody(d, type, booking, fields, accent, d.id === "answers" ? (props.answersPick ?? null) : null, st, ctx);
        if (body === null) return null;
        return (
          <div key={r.uid} onClick={(e) => { e.stopPropagation(); onSelect(on ? null : r.uid); }}
            onMouseEnter={() => setHover(r.uid)}
            onMouseLeave={() => setHover((x) => (x === r.uid ? null : x))}
            className="nj-section"
            style={{ position: "relative", padding: "11px 12px", borderRadius: 10, cursor: "pointer", border: `1px solid ${on ? "#5142C7" : "#EDEEF2"}`, outline: on ? "2px solid #5142C7" : "none", outlineOffset: 2, background: "#FFFFFF" }}>
            {on ? <span style={{ position: "absolute", top: -9, left: 9, background: "#5142C7", color: "#FFFFFF", fontSize: 8.5, fontWeight: 800, letterSpacing: "0.05em", borderRadius: 4, padding: "1px 7px" }}>SECTION · {d.label.toUpperCase()}</span> : null}
            {showCtl ? (
              <span onClick={(e) => e.stopPropagation()}
                style={{ position: "absolute", top: -11, right: 4, zIndex: 3, display: "inline-flex", gap: 3, background: "#FFFFFF", padding: 2, borderRadius: 6, boxShadow: "0 1px 4px rgba(20,19,31,0.16)" }}>
                {on ? (<>
                  <button onClick={() => nudge(r.uid, -1)} disabled={i === 0} title="Move up" style={{ ...CTL, opacity: i === 0 ? 0.35 : 1 }}>↑</button>
                  <button onClick={() => nudge(r.uid, 1)} disabled={i === state.length - 1} title="Move down" style={{ ...CTL, opacity: i === state.length - 1 ? 0.35 : 1 }}>↓</button>
                </>) : null}
                <button onClick={() => { if (on) onSelect(null); props.onChange!(csRemove(state, r.uid)); }}
                  title={`Remove ${d.label}`} style={{ ...CTL, borderColor: "#F3D0CB", color: "#C0392B", fontSize: 11 }}>×</button>
              </span>
            ) : null}
            {body}
          </div>
        );
  };

  const renderBlock = (blk: { uid: string; type: string; text?: unknown; href?: unknown }) => {
        /* LOCAL PATCH (platform side, 2026-09-09): the page's authored blocks
           (`content[]` — heading, text, callout, link, divider) round-trip through
           the store and the public lane but had no render arm on this canvas,
           so a block the operator saved never reached the customer. The
           designer previews them through `BlockCanvas`; this is the read-only
           twin for the live surface, same five kinds, same copy. */
        const text = typeof blk.text === "string" ? blk.text : "";
        if (blk.type === "divider") return <div key={blk.uid} style={{ height: 1, background: "#EDEEF2", margin: "2px 0" }} />;
        if (!text.trim()) return null;
        const card: React.CSSProperties = { padding: "11px 12px", borderRadius: 10, border: "1px solid #EDEEF2", background: "#FFFFFF", fontFamily: FONT };
        if (blk.type === "heading") return <div key={blk.uid} style={{ ...card, fontSize: 15, fontWeight: 800, color: "#14131F" }}>{text}</div>;
        if (blk.type === "callout") return <div key={blk.uid} style={{ ...card, background: "#FFF7E8", borderColor: "#F3D9A4", color: "#5A3E00", fontSize: 13, lineHeight: 1.55 }}>{text}</div>;
        if (blk.type === "link") {
          const href = confirmationBlockHref(blk);
          return (
            <div key={blk.uid} style={{ ...card, fontSize: 13 }}>
              {href ? (
                <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 700, textDecoration: "underline" }}>
                  {text.trim()}
                </a>
              ) : (
                <span style={{ color: "#3A3F4B" }}>{text}</span>
              )}
            </div>
          );
        }
        return <div key={blk.uid} style={{ ...card, fontSize: 13, color: "#3A3F4B", lineHeight: 1.55 }}>{text}</div>;
  };

  return (
    <div className="nj-sections" style={{ display: "flex", flexDirection: "column", gap: 10, fontFamily: FONT, fontSize: 14, lineHeight: 1.5 }}>
      {slots.map((slot) => (
        <Fragment key={`slot-${slot.index}`}>
          {slot.blocks.map(renderBlock)}
          {slot.row ? renderRow(slot.row, slot.index) : null}
        </Fragment>
      ))}
      {!ctx.live && shown.length === 0 ? empty(st === "expired"
        ? "An expired link shows only the brand header, the banner and the footer — none of those is on this page."
        : "Every section is switched off — the page would render empty.") : null}
      {st === "expired" && !ctx.live ? (
        /* Authoring annotation, and it was leaking onto the live 404 page — it
           explains the renderer's behaviour to an AUTHOR, and the customer
           reading "sections carrying booking data are withheld" learns only that
           something was hidden from them. Every other note() on this canvas is
           already live-gated; this one was written outside that helper and
           missed it. */
        <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>
          sections carrying booking data are withheld on an invalid link
        </div>
      ) : null}
      {ctx.live ? null : (
        <div style={{ fontSize: 9.5, color: "#B5BAC6", textAlign: "center", ...MONO }}>
          sample booking · a real one supplies these values
        </div>
      )}
    </div>
  );
}

/* ══ the inspector scope ═══════════════════════════════════════════════ */
export function CSInspector(props: {
  id: string;
  type: CSType;
  /** Types this page must serve, when that is more than `type`. Applicability
   *  unions across them; `type` alone still drives the preview. */
  applicableTypes?: CSType[];
  fields: CSPageFields;
  onPatch: (patch: CSPageFields) => void;
  hidden: boolean;
  onToggle: () => void;
}) {
  const d = csDef(props.id);
  if (!d) return null;
  const scope = props.applicableTypes && props.applicableTypes.length ? props.applicableTypes : [props.type];
  const appliesTo = scope.filter((type) => d.types.indexOf(type) > -1);
  const applies = appliesTo.length > 0;
  const appliesToSample = d.types.indexOf(props.type) > -1;
  const f = props.fields;
  return (
    <>
      <hr style={{ border: "none", borderTop: "1px solid #F4F5F7", margin: "4px 0 12px" }} />
      <div style={SEC}>{d.label} — section</div>
      <div style={{ ...NOTE, marginBottom: 10 }}>{d.help}</div>
      {applies && !appliesToSample ? (
        <div style={{ border: "1px solid #C7DDF7", background: "#F4F8FE", borderRadius: 8, padding: "9px 11px", fontSize: 11, lineHeight: 1.55, color: "#1B4C9E", marginBottom: 10 }}>
          This section renders for {appliesTo.join(" and ")} bookings on this page. The canvas is showing a {props.type} sample, so it is not visible here.
        </div>
      ) : !applies ? (
        <div style={{ border: "1px solid #F3D9A4", background: "#FFFAED", borderRadius: 8, padding: "9px 11px", fontSize: 11, lineHeight: 1.55, color: "#916D0F", marginBottom: 10 }}>
          This section means nothing on a {props.type} journey and will not render.
        </div>
      ) : null}
      {d.locked ? (
        <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.5, marginBottom: 10 }}>🔒 Locked on — it cannot be hidden or removed.</div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, marginBottom: 10 }}>
          {props.hidden ? "Hidden on this page" : "Shown on this page"}
          <button onClick={props.onToggle} style={{ border: "1px solid #D1D4DC", background: "#FFFFFF", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: FONT }}>{props.hidden ? "Show" : "Hide"}</button>
        </div>
      )}
      {(d.fields ?? []).indexOf("bannerTitle") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Headline</div>
          <input value={f.bannerTitle ?? ""} onChange={(e) => props.onPatch({ bannerTitle: e.target.value })} placeholder="You're all set" style={{ ...INPUT, fontSize: 12 }} />
          <div style={{ fontSize: 10, color: "#B5BAC6", marginTop: 4, lineHeight: 1.45 }}>Used when the outcome is a plain confirmation. Pending-approval and waitlisted wording is set by the outcome, not here.</div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("bannerSub") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Sub-line</div>
          <input value={f.bannerSub ?? ""} onChange={(e) => props.onPatch({ bannerSub: e.target.value })}
            placeholder="A confirmation is on its way to your email." style={{ ...INPUT, fontSize: 12 }} />
          <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 3 }}>
            Same rule as the headline: used on a plain confirmation only. Cancelled, past and waitlisted keep the outcome’s own wording.
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("cancelHours") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Cancellation window (hours)</div>
          <input value={f.cancelHours ?? ""} onChange={(e) => props.onPatch({ cancelHours: e.target.value })} placeholder="24" style={{ ...INPUT, fontSize: 12 }} />
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("rebookHours") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Rebooking window (hours)</div>
          <input value={f.rebookHours ?? ""} onChange={(e) => props.onPatch({ rebookHours: e.target.value })}
            placeholder={f.cancelHours || "24"} style={{ ...INPUT, fontSize: 12 }} />
          {/* Empty is a real answer, not a missing one: it means the tenant runs
              one window for both. The placeholder shows what Move will follow. */}
          <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 3 }}>
            When Move closes. Leave empty to use the cancellation window for both.
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("checkinCodeTarget") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>The code carries</div>
          {/* TWO SCANNERS, TWO ANSWERS (operator, 2026-09-14) — which is why this
              is a choice and not a constant:
                · The CODE carries the booking's record id (`bookingId`) for
                  every type, and My Day scans it to resolve the record. A URL is
                  noise to that reader. The saved key stays `reference` so stored
                  pages keep their choice, and the label follows the page's types.
                · A CUSTOMER scans it with a phone camera, which only knows how
                  to open a link. A bare reference does nothing there.
              Neither is the safe default for the other, so the author picks by
              naming who holds the scanner. */}
          {([
            (() => {
              const hasAppointment = scope.indexOf("appointment") > -1;
              const others = scope.filter((t) => t !== "appointment");
              /* The code carries the BOOKING'S ID for every type now
                 (CD, 2026-09-18), so the three labels no longer describe three
                 different payloads — they describe who scans it. The saved key
                 is still `reference`. */
              if (hasAppointment && !others.length) {
                return { v: "reference" as const, label: "Booking code", sub: "Holds the booking's id. Reception scans it in My Day to check the customer in." };
              }
              if (!hasAppointment) {
                return others.every((t) => t === "queue")
                  ? { v: "reference" as const, label: "Visit code", sub: "Holds the visit's id. Staff scan it in My Day to find the visit; the ticket number stays printed on the page." }
                  : { v: "reference" as const, label: "Booking code", sub: "Holds the booking's id. Staff scan it in My Day to find the booking; the reference stays printed on the page." };
              }
              return { v: "reference" as const, label: "Booking code", sub: "Holds the booking's id — an appointment, a registration or a queue visit alike. Staff scan it in My Day; the human reference stays printed on the page." };
            })(),
            { v: "manageUrl" as const, label: "Link to this page", sub: "A phone camera opens it. The link is the credential: whoever scans it can manage the booking." },
          ]).map((o) => {
            const chosen = (f.checkinCodeTarget ?? "reference") === o.v;
            return (
              <button key={o.v} type="button" onClick={() => props.onPatch({ checkinCodeTarget: o.v })}
                style={{
                  display: "block", width: "100%", textAlign: "left", marginBottom: 5, padding: "7px 9px",
                  borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                  border: `1px solid ${chosen ? "#5142C7" : "#D1D4DC"}`,
                  background: chosen ? "#F6F2FE" : "#FFFFFF",
                }}>
                <span style={{ display: "block", fontSize: 11.5, fontWeight: 700, color: chosen ? "#5142C7" : "#333333" }}>{o.label}</span>
                <span style={{ display: "block", fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 1 }}>{o.sub}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("summaryDateFormat") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Date format</div>
          <select value={f.summaryDateFormat ?? "auto"}
            onChange={(e) => props.onPatch({ summaryDateFormat: e.target.value })}
            style={{ ...INPUT, fontSize: 12 }}>
            {SUMMARY_DATE_FORMATS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 3 }}>
            A chosen format holds for every reader — that is what choosing one means. “Follow the reader’s device” is the default and the only one that varies.
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("summaryTimeFormat") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Clock</div>
          <div style={{ display: "flex", gap: 4 }}>
            {([
              { id: "auto", label: "Device" },
              { id: "h24", label: "14:30" },
              { id: "h12", label: "2:30 PM" },
            ]).map((o) => {
              const chosen = (f.summaryTimeFormat ?? "auto") === o.id;
              return (
                <button key={o.id} type="button" onClick={() => props.onPatch({ summaryTimeFormat: o.id })}
                  style={{
                    flex: 1, padding: "7px 6px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                    fontSize: 11.5, fontWeight: chosen ? 700 : 500,
                    border: `1px solid ${chosen ? "#5142C7" : "#D1D4DC"}`,
                    background: chosen ? "#F6F2FE" : "#FFFFFF",
                    color: chosen ? "#5142C7" : "#333333",
                  }}>{o.label}</button>
              );
            })}
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("summaryOff") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Rows on this page</div>
          {/* The rows of the PREVIEWED type. A page scoped to several types
              shows the one it is previewing, which is the one on the canvas
              beside it — a combined list would offer toggles for rows this
              preview cannot show, and the author could not check the result.

              THREE THINGS PER ROW, ONE LINE: on/off, its label, and where it
              sits. Order is dragged rather than nudged with arrows because the
              rows are few and short — the gesture is the same one the canvas
              uses, and it reads as the list it is editing. */}
          {summaryRowsFor(props.type, f.summaryOrder).map((r, i) => {
            const on = (f.summaryOff ?? []).indexOf(r.key) < 0;
            const order = summaryRowsFor(props.type, f.summaryOrder).map((x) => x.key);
            const flip = () => props.onPatch({ summaryOff: on
              ? [...(f.summaryOff ?? []), r.key]
              : (f.summaryOff ?? []).filter((x) => x !== r.key) });
            const move = (from: number, to: number) => {
              if (from === to || to < 0 || to >= order.length) return;
              const next = order.slice();
              next.splice(to, 0, next.splice(from, 1)[0]);
              props.onPatch({ summaryOrder: next });
            };
            return (
              <div key={r.key}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData("text/plain", String(i)); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                onDrop={(e) => { e.preventDefault(); move(Number(e.dataTransfer.getData("text/plain")), i); }}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
                <span title="Drag to reorder" aria-hidden="true"
                  style={{ cursor: "grab", color: "#B5BAC6", fontSize: 13, lineHeight: 1, flex: "none", letterSpacing: "-1px" }}>⁙</span>
                <span onClick={flip} role="switch" aria-checked={on} aria-label={r.label}
                  style={{ width: 32, height: 18, borderRadius: 999, background: on ? "#5142C7" : "#D8DBE2", position: "relative", flex: "none", cursor: "pointer" }}>
                  <span style={{ position: "absolute", top: 2, left: on ? 16 : 2, width: 14, height: 14, borderRadius: 999, background: "#FFFFFF" }} />
                </span>
                <input
                  value={(f.summaryLabels ?? {})[r.key] ?? ""}
                  placeholder={r.label}
                  aria-label={`Label for ${r.label}`}
                  onChange={(e) => props.onPatch({ summaryLabels: { ...(f.summaryLabels ?? {}), [r.key]: e.target.value } })}
                  onBlur={(e) => {
                    /* An empty override is not an empty label — it is no
                       override, so the key goes away and the default returns.
                       Storing "" would print a blank row title. */
                    if (e.target.value.trim()) return;
                    const next = { ...(f.summaryLabels ?? {}) };
                    delete next[r.key];
                    props.onPatch({ summaryLabels: next });
                  }}
                  style={{ ...INPUT, fontSize: 12, padding: "5px 8px", opacity: on ? 1 : 0.55 }} />
              </div>
            );
          })}
          <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 3 }}>
            Drag to reorder. Leave a label empty to keep the default. A row the booking has no value for still shows an em dash — switching it off is a different statement.
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("prepare") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Checklist — one item per line</div>
          {/* AUTHORED, and until 2026-09-14 it could not be. The section read
              `booking.prepare` off the page document and the customer's page
              rendered it faithfully — but nothing wrote it, so the designer
              showed a hardcoded sample and every real page rendered the empty
              state. The inspector said "nothing to configure", which was true
              and was the bug. */}
          <textarea
            value={(f.prepare ?? []).join("\n")}
            onChange={(e) => props.onPatch({ prepare: e.target.value.split("\n") })}
            onBlur={(e) => props.onPatch({ prepare: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean) })}
            rows={4}
            placeholder={"Photo ID\nProof of address"}
            style={{ ...INPUT, fontSize: 12, resize: "vertical", lineHeight: 1.5 }} />
          <div style={{ fontSize: 10.5, color: "#697180", lineHeight: 1.45, marginTop: 3 }}>
            Empty lines are dropped. No items means the section renders nothing.
          </div>
        </div>
      ) : null}
      {(d.fields ?? []).indexOf("note") > -1 ? (
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Footer note</div>
          <textarea value={f.note ?? ""} onChange={(e) => props.onPatch({ note: e.target.value })} rows={3} style={{ ...INPUT, fontSize: 12, resize: "vertical" }} />
        </div>
      ) : null}
      {!d.fields?.length ? (
        <div style={{ fontSize: 11, color: "#697180", lineHeight: 1.5 }}>
          Nothing to configure — this section renders the booking. {cap(props.type)} journeys can use{" "}
          {csForType(props.type).length} of the {CONFIRMATION_SECTIONS.length} sections.
        </div>
      ) : null}
    </>
  );
}
